/**
 * Whether the page can reach Occurra Cloud. A failed request or a down stream only starts the
 * probes, and only the probes decide: parallel requests fail together, and the probes are
 * sequential, so ten failed probes are ten checks over time.
 */

import { SECOND_MS } from '@/lib/timeUnits';
import type { Unsubscribe } from '@/types/common';
import { PortalEvent } from '@/types/portal';

import { CONFIG_URL } from './bootConfig';
import { HTTP_STATUS } from './httpStatus';

export const FAILED_PROBES_TO_UNAVAILABLE = 10;
const PROBE_TIMEOUT_MS = 3 * SECOND_MS;
const DETECT_INTERVAL_MS = 3 * SECOND_MS;
const RECOVER_INTERVAL_MS = 5 * SECOND_MS;
// A slow media server makes a relay request time out while the cloud answers.
const RELAY_PATH_PREFIX = '/relay/proxy/';
// The relay answers these for a media server that is away, and the gateway for a backend that
// is away: neither says that the cloud answers.
const GATEWAY_STATUSES: readonly number[] = [
    HTTP_STATUS.BAD_GATEWAY,
    HTTP_STATUS.SERVICE_UNAVAILABLE,
    HTTP_STATUS.GATEWAY_TIMEOUT,
];

enum PortalState {
    Reachable,
    Probing,
    Unavailable,
}

interface PortalMonitorDeps {
    /** True when the cloud answered. */
    probe: () => Promise<boolean>;
}

function isRelayRequest(url: string): boolean {
    // The base only resolves a relative URL; the path is all that is read.
    return new URL(url, 'http://localhost').pathname.startsWith(RELAY_PATH_PREFIX);
}

function isTimeout(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'TimeoutError';
}

/** One probe of the config endpoint, which answers without a sign-in. */
export async function probePortal(): Promise<boolean> {
    try {
        const resp = await fetch(CONFIG_URL, { signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) });
        const contentType = resp.headers.get('Content-Type') ?? '';
        // A page served in place of the API is not the cloud.
        return resp.status === HTTP_STATUS.OK && contentType.includes('application/json');
    } catch {
        return false;
    }
}

export class PortalMonitor {
    private readonly probe: () => Promise<boolean>;

    private readonly listeners = new Set<(event: PortalEvent) => void>();

    private state = PortalState.Reachable;

    private failedProbes = 0;

    private probeTimer: ReturnType<typeof setTimeout> | undefined;

    private isProbeInFlight = false;

    /** Changes at each return to Reachable, so that the answer of an older probe is dropped. */
    private round = 0;

    constructor(deps: PortalMonitorDeps) {
        this.probe = deps.probe;
    }

    isUnavailable(): boolean {
        return this.state === PortalState.Unavailable;
    }

    /** Tells `listener` each event; while the cloud is unavailable, it hears that at once. */
    subscribe(listener: (event: PortalEvent) => void): Unsubscribe {
        this.listeners.add(listener);
        if (this.isUnavailable()) {
            listener(PortalEvent.Unavailable);
        }
        return (): void => {
            this.listeners.delete(listener);
        };
    }

    onRecovered(onRecovered: () => void): Unsubscribe {
        return this.subscribe((event): void => {
            if (event === PortalEvent.Recovered) {
                onRecovered();
            }
        });
    }

    reportResponse(status: number): void {
        if (!GATEWAY_STATUSES.includes(status)) {
            this.markReachable();
        }
    }

    reportError(url: string, error: unknown): void {
        if (error instanceof TypeError || (isTimeout(error) && !isRelayRequest(url))) {
            this.startProbing();
        }
    }

    reportStreamOpen(): void {
        this.markReachable();
    }

    reportStreamDown(): void {
        this.startProbing();
    }

    probeNow(): void {
        if (this.state === PortalState.Reachable) {
            return;
        }
        clearTimeout(this.probeTimer);
        this.probeTimer = undefined;
        this.runProbe();
    }

    private startProbing(): void {
        if (this.state !== PortalState.Reachable) {
            return;
        }
        this.state = PortalState.Probing;
        this.failedProbes = 0;
        this.runProbe();
    }

    private runProbe(): void {
        if (this.isProbeInFlight) {
            return;
        }
        this.isProbeInFlight = true;
        const round = this.round;
        this.probe().then(
            (isAnswered): void => this.onProbeAnswer(round, isAnswered),
            (): void => this.onProbeAnswer(round, false),
        );
    }

    private onProbeAnswer(round: number, isAnswered: boolean): void {
        this.isProbeInFlight = false;
        if (round !== this.round) {
            // A success ended that round. A round that started since waits for no other probe.
            if (this.state !== PortalState.Reachable) {
                this.runProbe();
            }
            return;
        }
        if (isAnswered) {
            this.markReachable();
            return;
        }
        this.failedProbes += 1;
        if (this.state === PortalState.Probing &&
            this.failedProbes >= FAILED_PROBES_TO_UNAVAILABLE) {
            this.state = PortalState.Unavailable;
            this.announce(PortalEvent.Unavailable);
        }
        const delayMs = this.isUnavailable() ? RECOVER_INTERVAL_MS : DETECT_INTERVAL_MS;
        this.probeTimer = setTimeout((): void => {
            this.probeTimer = undefined;
            this.runProbe();
        }, delayMs);
    }

    private markReachable(): void {
        if (this.state === PortalState.Reachable) {
            return;
        }
        const wasUnavailable = this.isUnavailable();
        clearTimeout(this.probeTimer);
        this.probeTimer = undefined;
        this.round += 1;
        this.state = PortalState.Reachable;
        this.failedProbes = 0;
        if (wasUnavailable) {
            this.announce(PortalEvent.Recovered);
        }
        this.announce(PortalEvent.Answered);
    }

    private announce(event: PortalEvent): void {
        for (const listener of [...this.listeners]) {
            try {
                listener(event);
            } catch {
                // One subscriber must not stop the rest.
            }
        }
    }
}

export const portalMonitor = new PortalMonitor({ probe: probePortal });
